import IconButton from '@/global/components/Button/IconButton'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import dayjs from 'dayjs'
import { Button } from 'global/components/Button'
import { SelectEmpresa } from 'global/components/Selects'
import ContentGuide from 'global/guides/ContentGuide'
import { hasPermission } from 'hooks/useBackendMenu'
import Cookies from 'js-cookie'
import { DownloadIcon, FilePlusIcon, Trash2Icon } from 'lucide-react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { EspelhoPeriodo, espelhoPeriodo } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'

const STATUS_LABEL: Record<EspelhoPeriodo['status'], string> = {
    processando: 'Processando',
    pronto: 'Pronto',
    erro: 'Erro',
}

export default function RelatorioEspelhoPeriodo() {
    const empresaId = useStore(state => state.companyId)
    const { data = [], isLoading } = espelhoPeriodo.useFetch({
        queryParams: { empresaId },
    })

    return (
        <ContentGuide>
            <ContentGuide.Card>
                <ContentGuide.Header
                    label={['Relatórios', 'Espelho de ponto por período']}
                >
                    <SelectEmpresa hideWhenSingle />
                </ContentGuide.Header>
                <ContentGuide.DataGrid
                    loading={isLoading}
                    columns={[
                        {
                            field: 'filename',
                            headerName: 'Nome do arquivo',
                            flex: 1,
                        },
                        {
                            field: 'dataInicial',
                            headerName: 'Data inicial',
                            width: 160,
                            align: 'right',
                            headerAlign: 'right',
                            valueFormatter: params =>
                                dayjs(params.value).format('DD/MM/YYYY'),
                        },
                        {
                            field: 'dataFinal',
                            headerName: 'Data final',
                            width: 160,
                            align: 'right',
                            headerAlign: 'right',
                            valueFormatter: params =>
                                dayjs(params.value).format('DD/MM/YYYY'),
                        },
                        {
                            field: 'status',
                            headerName: 'Situação',
                            width: 140,
                            valueFormatter: params =>
                                STATUS_LABEL[
                                    params.value as EspelhoPeriodo['status']
                                ],
                        },
                        {
                            field: 'createdAt',
                            headerName: 'Data de criação',
                            width: 200,
                            align: 'right',
                            headerAlign: 'right',
                            valueFormatter: params =>
                                dayjs(params.value).format('DD/MM/YYYY HH:mm'),
                        },
                        {
                            ...optionsColumn,
                            field: '__baixar',
                            renderCell: ({ row }) =>
                                row.status === 'pronto' && row.filename ? (
                                    <IconButton
                                        title="Baixar relatório"
                                        onClick={() =>
                                            download(row.id, row.filename)
                                        }
                                    >
                                        <DownloadIcon />
                                    </IconButton>
                                ) : null,
                        },
                        {
                            ...optionsColumn,
                            field: '__excluir',
                            renderCell: ({ id }) =>
                                hasPermission(
                                    ['delete', 'EspelhoPontoPorPeríodo'],
                                    () => (
                                        <IconButton
                                            title="Excluir"
                                            href={`${id}/excluir`}
                                        >
                                            <Trash2Icon className="text-red-500" />
                                        </IconButton>
                                    ),
                                ),
                        },
                    ]}
                    rows={data}
                    ToolbarProps={{
                        exportButtonProps: {
                            fileName: 'exportacao-espelho-periodo',
                        },
                    }}
                    components={{
                        Toolbar: () =>
                            hasPermission([
                                'create',
                                'EspelhoPontoPorPeríodo',
                            ]) ? (
                                <>
                                    <div style={{ flex: 1 }} />
                                    <Button
                                        startIcon={<FilePlusIcon />}
                                        href="relatorio"
                                    >
                                        Gerar relatório...
                                    </Button>
                                </>
                            ) : null,
                    }}
                />
            </ContentGuide.Card>
            <Outlet context={{ data }} />
        </ContentGuide>
    )
}

/**
 * Baixa pelo id do relatório: a empresa vem do próprio id no backend, e não da
 * empresa selecionada agora na tela, que pode ter mudado desde a geração.
 */
async function download(id: string, filename: string) {
    const url = `${import.meta.env.VITE_BASE_URL}/v1/espelho-periodo/${encodeURIComponent(
        id,
    )}/arquivo`
    const token = Cookies.get('JWT')

    try {
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })

        // Sem esta checagem o corpo do erro era salvo como se fosse o ZIP.
        if (!response.ok) {
            const erro = await response.json().catch(() => null)

            throw new Error(
                erro?.message || 'Não foi possível baixar o arquivo',
            )
        }

        const blob = await response.blob()
        const objectUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = objectUrl
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()

        link.parentNode?.removeChild(link)
        window.URL.revokeObjectURL(objectUrl)
    } catch (error) {
        toast.error(
            error instanceof Error
                ? error.message
                : 'Não foi possível baixar o arquivo',
        )
    }
}

export function useEspelhoPeriodoOutletContext() {
    return useOutletContext<{
        data: EspelhoPeriodo[]
    }>()
}
